import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { Typography, Spin, Tooltip, Checkbox, Space } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { path } from 'ramda';
import moment from 'moment';

import Button from 'components/molecules/button';
import Input from 'components/organisms/input';
import { useIntl } from 'common/config/i18n';
import { StationsSelectors, UserInfoSelectors, customersListSelectors } from 'common/config/store';
import { StyledBlueLink } from 'common/styling/mixins';
import { FluxClientsFilters } from 'types';
import FiltersCard from 'components/organisms/filters-card';
import { isFrance } from 'common/config/env';
import { Translate } from 'core/i18n/types';
import InputSelect from 'components/organisms/select';
import DatePickerForMomentDates from 'components/organisms/datepicker';

const { Text } = Typography;

type FilterMode = 'customersByStation' | 'customersByOD';
interface Props {
  fetchAction: (params: FluxClientsFilters) => void;
  refreshAction: (params: FluxClientsFilters) => void;
  exportListAction: (params: FluxClientsFilters) => void;
  status?: 'loading' | 'error' | 'success';
  searchDate?: string;
  mode?: FilterMode;
}

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const displayDate = (t: ReturnType<Translate>, dateStr: string) =>
  `${t('filtre', 'REFRESH_LABEL')} ${`${t('filtre', 'REFRESH_LABEL_DAY')} ${moment(dateStr).format('DD/MM/YYYY')}`} ${t(
    'filtre',
    'REFRESH_LABEL_TIME'
  )} ${moment(dateStr).format('HH:mm:ss')}`;

const formikInitialValues = {
  serviceName: undefined,
  station: undefined,
  departureStation: undefined,
  arrivalStation: undefined,
  startDate: moment().format(),
  endDate: moment().format(),
  includedRolling: undefined,
  includedCancel: undefined,
  serviceTypeIDs: undefined,
};

const CustomersFilter = (props: Props) => {
  const { t } = useIntl();

  const filterValues = useSelector(customersListSelectors.filtersSelector);
  const stationsStatus = useSelector(StationsSelectors.statusSelector);
  const originStations = useSelector(StationsSelectors.originStationsSelector);
  const actifUserServiceTypes = useSelector(UserInfoSelectors.getActifUserServiceTypesSelector);

  const formik = useFormik({
    initialValues: formikInitialValues,
    onSubmit: (values) => props.fetchAction(values),
  });

  useEffect(() => {
    formik.setFieldValue('serviceName', filterValues?.serviceName);
    formik.setFieldValue('station', filterValues?.station); // for mode === 'customersByStation'
    formik.setFieldValue('departureStation', filterValues?.departureStation); // for mode === 'customersByOD'
    formik.setFieldValue('arrivalStation', filterValues?.arrivalStation); // for mode === 'customersByOD'
    formik.setFieldValue('startDate', filterValues?.startDate || moment().format());
    formik.setFieldValue('endDate', filterValues?.endDate || moment().format());
    formik.setFieldValue('includedRolling', isFrance ? !!filterValues?.includedRolling : undefined); // includeRolling exists if isFrance
    formik.setFieldValue('includedCancel', !!filterValues?.includedCancel);
    formik.setFieldValue('serviceTypeIDs', filterValues?.serviceTypeIDs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const onReset = () => {
    formik.setFieldValue('serviceName', undefined);
    formik.setFieldValue('startDate', moment().format());
    formik.setFieldValue('endDate', moment().format());
    formik.setFieldValue('station', undefined); // for mode === 'customersByStation'
    formik.setFieldValue('departureStation', undefined); // for mode === 'customersByOD'
    formik.setFieldValue('arrivalStation', undefined); // for mode === 'customersByOD'
    formik.setFieldValue('includedRolling', isFrance ? false : undefined); // includeRolling exists if isFrance
    formik.setFieldValue('includedCancel', false);
    formik.setFieldValue('serviceTypeIDs', undefined);
    props.fetchAction({
      startDate: moment().format(),
      endDate: moment().format(),
      includedRolling: isFrance ? false : undefined, // includeRolling exists if isFrance
      includedCancel: false,
    });
  };

  const dateToDisplay: string = props.searchDate ? displayDate(t, props.searchDate) : '';
  const customersMode = props.mode || 'customersByStation';

  const inputSelectStationsData = [
    {
      id: 'stations',
      list: originStations?.map((station) => ({ id: station.uicStationCode, name: station.name })) || [],
    },
  ];

  const inputSelectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        actifUserServiceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  const CheckboxIncludedRolling = () => (
    <Tooltip title={t('filtre', 'INCLUDED_ROLLING_TOOLTIP')} placement="right">
      <CheckboxWrapper>
        <UpLabel>{t('filtre', 'INCLUDED_ROLLING')}</UpLabel>
        <Checkbox
          onChange={(e) => formik.setFieldValue('includedRolling', e.target.checked)}
          checked={formik.values.includedRolling}
        />
      </CheckboxWrapper>
    </Tooltip>
  );

  const StartEndDates = () => (
    <Space direction="horizontal" size="middle" align="center" wrap>
      <div>
        <UpLabel>{t('filtre', 'SEARCH_FILTER_DEPARTURE_DATE')}</UpLabel>
        <DatePickerForMomentDates
          allowClear={false}
          value={moment(formik.values.startDate)}
          format="DD/MM/YYYY"
          style={{ width: '150px', borderRadius: '5px' }}
          onChange={(date) => {
            formik.setFieldValue('startDate', date);
            if (moment(date).isAfter(moment(formik.values.endDate))) formik.setFieldValue('endDate', date);
          }}
        />
      </div>
      <div>
        <UpLabel>{t('filtre', 'SEARCH_FILTER_END_DATE')}</UpLabel>
        <DatePickerForMomentDates
          defaultValue={moment(formik.values.startDate)}
          allowClear={false}
          disabledDate={(current) => {
            return current && current < moment(formik.values.startDate);
          }}
          value={moment(formik.values.endDate)}
          style={{ width: '150px', borderRadius: '5px' }}
          format="DD/MM/YYYY"
          onChange={(date) => formik.setFieldValue('endDate', date)}
        />
      </div>
    </Space>
  );

  const Station = () => (
    <div>
      <UpLabel>{t('form', 'FORM_STATIONS')}</UpLabel>
      <InputSelect
        onChange={(value) => formik.setFieldValue('station', value)}
        value={formik.values.station}
        data={inputSelectStationsData}
        style={{ minWidth: '150px', borderRadius: '5px' }}
        dropdownStyle={{ minWidth: '20%' }}
        popupMatchSelectWidth
        showSearch
        allowClear
      />
    </div>
  );

  const ODStations = () => (
    <Space direction="horizontal" size="middle" align="center" wrap>
      <div>
        <UpLabel>{t('filtre', 'SEARCH_FILTER_DEPARTURE_STATION')}</UpLabel>
        <InputSelect
          onChange={(value) => formik.setFieldValue('departureStation', value)}
          value={formik.values.departureStation}
          data={inputSelectStationsData}
          style={{ minWidth: '150px', borderRadius: '5px' }}
          dropdownStyle={{ minWidth: '20%' }}
          popupMatchSelectWidth
          showSearch
          allowClear
        />
      </div>
      <div>
        <UpLabel>{t('filtre', 'SEARCH_FILTER_ARRIVAL_STATION')}</UpLabel>
        <InputSelect
          onChange={(value) => formik.setFieldValue('arrivalStation', value)}
          value={formik.values.arrivalStation}
          data={inputSelectStationsData}
          style={{ minWidth: '150px', borderRadius: '5px' }}
          dropdownStyle={{ minWidth: '20%' }}
          popupMatchSelectWidth
          showSearch
          allowClear
        />
      </div>
    </Space>
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('filtre', 'SEARCH_FILTER_TITLE')}
        firstPart={
          <>
            <Input
              width="150px"
              upLabel={t('filtre', 'SEARCH_FILTER_TRAIN_NUMBER')}
              name="serviceName"
              onChange={formik.handleChange}
              value={formik.values.serviceName}
            />

            {!!isFrance && <CheckboxIncludedRolling />}

            {customersMode === 'customersByStation' && <Station />}

            {customersMode === 'customersByOD' && <ODStations />}

            <StartEndDates />

            <CheckboxWrapper>
              <UpLabel>{t('filtre', 'INCLUDED_CANCEL')}</UpLabel>
              <Checkbox
                onChange={(e) => formik.setFieldValue('includedCancel', e.target.checked)}
                checked={formik.values.includedCancel}
              />
            </CheckboxWrapper>

            {actifUserServiceTypes.length > 1 && (
              <div>
                <UpLabel>{t('filtre', 'HISTO_TYPE_CIRCULATION')}</UpLabel>
                <InputSelect
                  onChange={(ids) => {
                    formik.setFieldValue('serviceTypeIDs', ids);
                  }}
                  value={formik.values.serviceTypeIDs}
                  data={inputSelectServiceTypesData}
                  style={{ minWidth: '200px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  showSearch
                  valueAsArray
                />
              </div>
            )}
          </>
        }
        secondPart={
          <>
            <Button
              loading={stationsStatus === 'loading'}
              fontSize="1.2rem"
              borderRadius="20px"
              htmlType="submit"
              width="200px"
            >
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={onReset}>{t('filtre', 'SEARCH_FILTER_RESET_BUTTON')}</StyledBlueLink>
          </>
        }
        thirdPart={
          <>
            <p className="searchDate">
              {dateToDisplay && <Text>{dateToDisplay}</Text>}
              <Tooltip title={t('filtre', 'SEARCH_FILTER_REFRESH_BUTTON_TOOLTIP')} placement="right">
                <StyledBlueLink
                  onClick={() => props.refreshAction(formik.values)}
                  className="refreshLink"
                  style={{ display: 'inline', margin: 0, fontSize: 'inherit', marginLeft: 10 }}
                >
                  {t('filtre', 'SEARCH_FILTER_REFRESH_BUTTON')}
                </StyledBlueLink>
              </Tooltip>
            </p>
            {props.status === 'loading' && <Spin />}
          </>
        }
        topRightPart={
          <FileExcelOutlined
            style={{ fontSize: 40, color: 'rgb(35,127,78)' }}
            onClick={() => props.exportListAction(formik.values)}
          />
        }
      />
    </form>
  );
};
export default CustomersFilter;
